import { BlurView } from 'expo-blur';
import { groupBy } from 'lodash';
import moment from 'moment';
import { Box, Divider, Heading, ScrollView } from 'native-base';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshControl } from 'react-native';
import { useActivityData, useCampaign, useScheduleItems } from '../../hooks';
import { Activity } from '../../models/view';
import { useAppDispatch } from '../../store';
import { campaignActions, graduwayEventActions, programEventActions } from '../../store/actions';
import { colors } from '../../styles';
import { isWeb, MAX_WEB_CONTAINER_WIDTH } from '../../utils';
import { nativeAlert } from '../../utils/ui';
import { NoData } from '../NoData';
import { ActivityCard } from '../ui';

export interface ScheduleListItemProps {
  activity: Activity;
  onPress: () => void;
}

export const ScheduleListItem: React.FC<ScheduleListItemProps> = ({ activity, onPress }) => {
  const { date, time } = useActivityData(activity);

  return (
    <ActivityCard
      title={activity.title}
      overline={date}
      content={time}
      onPress={onPress}
      thumbnailUrl={activity.imageUrl}
    />
  );
};

interface GroupedSection {
  title: string;
  data: Activity[];
}

const groupAndSortItems = (items: Activity[], timezone: string): GroupedSection[] => {
  const now = moment().tz(timezone).startOf('day');

  const filtered = items
    .filter((item) => {
      const startDate = moment(item.dateTime).tz(timezone).startOf('day');
      return startDate.isSameOrAfter(now);
    })
    .sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime());

  return Object.entries(
    groupBy(filtered, (item) => {
      return new Date(item.dateTime).toLocaleDateString('en-US', { month: 'long' });
    }),
  ).map(([month, items]) => ({
    title: month,
    data: items,
  }));
};

interface EventSectionProps {
  sections: GroupedSection[];
  onItemPress: (item: Activity) => void;
}

const EventSection: React.FC<EventSectionProps> = ({ sections, onItemPress }) => {
  return (
    <>
      {sections.map((section) => (
        <Box key={section.title}>
          <Box px={4}>
            <BlurView style={{ width: '100%', height: 40, flex: 1, justifyContent: 'flex-end', paddingBottom: 10 }}>
              <Heading fontSize={12} fontWeight={'700'} color={colors.darkGrey} textTransform="uppercase">
                {section.title}
              </Heading>
            </BlurView>
          </Box>
          {section.data.map((item) => (
            <Box key={item.id} px={4}>
              <ScheduleListItem activity={item} onPress={() => onItemPress(item)} />
            </Box>
          ))}
        </Box>
      ))}
    </>
  );
};

export interface ScheduleListProps {
  onItemPress: (item: Activity) => void;
}

export const ScheduleList: React.FC<ScheduleListProps> = ({ onItemPress }) => {
  const dispatch = useAppDispatch();
  const { campaign } = useCampaign();
  const { programEventItems, graduwayEventItems, loading, error } = useScheduleItems();
  const [handledError, setHandledError] = useState(true);

  useEffect(() => {
    if (error && !handledError) {
      setHandledError(true);
      nativeAlert('Loading Error', 'There was a problem loading your schedule. Please try again.');
    }
  }, [handledError, error]);

  const timezone = campaign?.timezone ?? 'UTC';

  const groupedProgramEvents = useMemo(
    () => groupAndSortItems(programEventItems, timezone),
    [programEventItems, timezone],
  );

  const groupedGraduwayEvents = useMemo(
    () => groupAndSortItems(graduwayEventItems, timezone),
    [graduwayEventItems, timezone],
  );

  const hasNoEvents = groupedProgramEvents.length === 0 && groupedGraduwayEvents.length === 0;

  const refresh = useCallback(() => {
    dispatch(programEventActions.getProgramEvents());
    dispatch(graduwayEventActions.getGraduwayEvents());
    dispatch(campaignActions.getCampaign());

    setTimeout(() => {
      setHandledError(false);
    }, 1000);
  }, [dispatch]);

  return (
    <ScrollView
      w="100%"
      h="100%"
      maxWidth={isWeb ? MAX_WEB_CONTAINER_WIDTH : undefined}
      contentContainerStyle={{ paddingBottom: 24 }}
      refreshControl={<RefreshControl refreshing={loading} onRefresh={refresh} />}
    >
      <Heading py={4} px={4}>
        Schedule
      </Heading>

      {hasNoEvents ? (
        <NoData px={4} message="No upcoming events. Check back soon..." />
      ) : (
        <>
          {groupedProgramEvents.length > 0 && (
            <Box>
              <Heading fontSize={16} fontWeight={'600'} px={4} pb={2}>
                Program Sessions
              </Heading>
              <EventSection sections={groupedProgramEvents} onItemPress={onItemPress} />
            </Box>
          )}

          {groupedProgramEvents.length > 0 && groupedGraduwayEvents.length > 0 && (
            <Divider my={4} bg={colors.lightGrey} />
          )}

          {groupedGraduwayEvents.length > 0 && (
            <Box>
              <Heading fontSize={16} fontWeight={'600'} px={4} pb={2}>
                Career Center Events
              </Heading>
              <EventSection sections={groupedGraduwayEvents} onItemPress={onItemPress} />
            </Box>
          )}
        </>
      )}
    </ScrollView>
  );
};
